import React from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { I18n } from 'react-redux-i18n';
import { formatTimeElapsed } from '../../../utils/time/formatTimeElapsed';
import styles from './GiftMatchWidget.module.css';

export interface GiftMatch {
  id: string | number;
  name: string;
  status: 'live' | 'planned' | 'complete';
  totalRaised: number;
  matchGoal: number | null;
  matchGoalType: 'amount' | 'count';
  startTime: Date | null;
  endTime: Date | null;
  multiplier: number;
  donationMatchedTotalAmount: number;
  donationMatchedCount: number;
}

export interface GiftMatchWidgetProps {
  matches: GiftMatch[];
  selectedIndex: number;
  onSelectIndex: (index: number) => void;
  formatCurrency?: (amount: number) => string | React.ReactNode;
  formatNumber?: (num: number) => string;
}

const defaultFormatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const defaultFormatNumber = (num: number): string => {
  return num.toLocaleString();
};

/**
 * GiftMatchWidget - Display gift matching campaign information with status and progress
 * 
 * Features:
 * - Live/Planned/Complete status indicators
 * - Progress bar with percentage
 * - Multiple match navigation
 * - Countdown timers
 * - Custom formatters for currency and numbers
 */
export const GiftMatchWidget: React.FC<GiftMatchWidgetProps> = ({
  matches,
  selectedIndex,
  onSelectIndex,
  formatCurrency = defaultFormatCurrency,
  formatNumber = defaultFormatNumber
}) => {
  if (!matches || matches.length === 0) return null;

  const currentMatch = matches[selectedIndex] || matches[0];
  const goal = Number(currentMatch.matchGoal) || 0;
  const raised = Number(currentMatch.totalRaised) || 0;
  const percentageRaised = goal > 0 ? (raised / goal) * 100 : 0;
  const amountRemaining = Math.max(goal - raised, 0);
  const isAmountType = currentMatch.matchGoalType === 'amount';
  const isCountType = currentMatch.matchGoalType === 'count';

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'live':
        return {
          label: I18n.t('campaign.performance.gift-match.live'),
          isLive: true,
          textColor: styles.liveText,
        };
      case 'planned':
        return {
          label: I18n.t('campaign.performance.gift-match.starts'),
          isLive: false,
          textColor: styles.metricIconBlue,
        };
      case 'complete':
        return {
          label: 'COMPLETE',
          isLive: false,
          textColor: styles.metricIconTeal,
        };
      default:
        return { label: '', isLive: false, textColor: '' };
    }
  };

  const statusConfig = getStatusConfig(currentMatch.status);

  const formatDateWithTime = (date: Date | null) => {
    if (!date) return '—';
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const isMidnight = hours === 0 && minutes === 0;

    if (isMidnight) {
      return date.toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    }
    const datePart = date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
    const timePart = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    return `${datePart}, ${timePart}`;
  };

  const formatCountdown = (targetTime: Date | null, suffix: string) => {
    if (!targetTime) return '';
    const now = new Date();
    const diff = targetTime.getTime() - now.getTime();

    if (diff <= 0) return '';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return `${days}d ${remainingHours}hr ${minutes}min ${suffix}`;
    }
    return `${hours}hr ${minutes}min ${suffix}`;
  };

  type MatchDisplayInfo = {
    mainText: string;
    mainDate: string;
    countdown?: string;
    additionalText?: string;
  };

  const getMatchDisplayInfo = (): MatchDisplayInfo | null => {
    const { status, startTime, endTime } = currentMatch;

    if (status === 'live') {
      if (startTime) {
        return {
          mainText: I18n.t('campaign.performance.gift-match.started'),
          mainDate: formatDateWithTime(startTime)
        };
      }
      if (endTime) {
        return {
          mainText: I18n.t('campaign.performance.gift-match.ends'),
          mainDate: formatDateWithTime(endTime),
          countdown: formatCountdown(endTime, 'to completion')
        };
      }
    }

    if (status === 'planned' && startTime) {
      return {
        mainText: I18n.t('campaign.performance.gift-match.starts'),
        mainDate: formatDateWithTime(startTime),
        countdown: formatCountdown(startTime, 'to begin')
        };
    }

    if (status === 'complete' && startTime) {
      return {
        mainText: I18n.t('campaign.performance.gift-match.started'),
        mainDate: startTime.toLocaleDateString(),
        ...(endTime && {
          additionalText: `${I18n.t('campaign.performance.gift-match.ran-for')} ${formatTimeElapsed(startTime, endTime, {
            includeSuffix: false
          })}`
        })
      };
    }

    return null;
  };

  const displayInfo = getMatchDisplayInfo();
  const hasValidGoal = goal > 0 && (isAmountType || isCountType);

  return (
    <div className={`${styles.giftMatchWidget} ${currentMatch.status === 'live' ? styles.giftMatchWidgetLive : ''}`}>
      <div className={styles.giftMatchHeader}>
        <div className={styles.giftMatchTitle}>
          <h3>
            {currentMatch.status === 'live' ? '🎉 ' : ''}{I18n.t('campaign.performance.gift-match.title')} - {currentMatch.name}
          </h3>
          {currentMatch.status === 'live' && (
            <div className={styles.liveIndicator}>
              <div className={styles.pulseDot}>
                <div className={styles.pulseDotOuter}></div>
                <div className={styles.pulseDotInner}></div>
              </div>
              <span className={styles.liveText}>{statusConfig.label}</span>
            </div>
          )}
        </div>

        {matches.length > 1 && (
          <div className={styles.giftMatchNavigation}>
            <button
              onClick={() => onSelectIndex(Math.max(0, selectedIndex - 1))}
              disabled={selectedIndex === 0}
              className={styles.navButton}
              aria-label="Previous match"
            >
              <ChevronLeft className={styles.icon} />
            </button>
            <span className={styles.navText}>
              {selectedIndex + 1} of {matches.length}
            </span>
            <button
              onClick={() => onSelectIndex(Math.min(matches.length - 1, selectedIndex + 1))}
              disabled={selectedIndex === matches.length - 1}
              className={styles.navButton}
              aria-label="Next match"
            >
              <ChevronRight className={styles.icon} />
            </button>
          </div>
        )}
      </div>

      <div className={styles.giftMatchAmount}>
        <div className={styles.amountRow}>
          <div className={styles.primaryAmount}>
            {isAmountType ? formatCurrency(raised) : formatNumber(raised)}
          </div>
          {hasValidGoal && (
            <div className={styles.targetAmount}>
              of {isAmountType ? formatCurrency(goal) : formatNumber(goal)}
            </div>
          )}
        </div>
        <div className={styles.giftMatchMeta}>
          {displayInfo && (
            <>
              <div className={styles.metaItem}>
                <Calendar className={styles.icon} />
                <span>{displayInfo.mainText} {displayInfo.mainDate}</span>
              </div>
              {(displayInfo.countdown || displayInfo.additionalText) && (
                <>
                  <span>•</span>
                  <span>{displayInfo.countdown || displayInfo.additionalText}</span>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className={styles.progressSection}>
        <div className={styles.progressBar}>
          <div
            className={`${styles.progressFill} ${
              currentMatch.status === 'complete' ? styles.progressFillComplete :
              currentMatch.status === 'live' ? styles.progressFillLive : styles.progressFillPlanned
            }`}
            style={{ width: `${Math.min(percentageRaised, 100)}%` }}
          />
        </div>
        <div className={styles.progressMeta}>
          <span className={styles.progressLeft}>
            {Math.round(percentageRaised)}% {I18n.t('campaign.performance.gift-match.matched')}
          </span>
          {currentMatch.status === 'live' && currentMatch.endTime && (
            <span className={`${styles.progressRight} ${styles.progressRightLive}`}>
              {formatCountdown(currentMatch.endTime, 'left')}
            </span>
          )}
          {currentMatch.status === 'planned' && (
            <span className={styles.progressRight}>
              {isAmountType ? formatCurrency(amountRemaining) : formatNumber(amountRemaining)} {I18n.t('campaign.performance.gift-match.to-match')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
